import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";

import {
  advanceTipProgress,
  INITIAL_TIP_PROGRESS,
  tutorialGuidance,
  type TipProgress,
  type TutorialGuidance,
} from "../game/tutorialGuidance";
import { skipTutorialGame } from "../game/tutorialSkip";
import { useGameStore } from "../stores/useGameStoreFacade";
import { useGameplayStore } from "../game/stores/useGameplayStore";
import { unifiedDataStore } from "../services/UnifiedDataStore";

interface TutorialContextType {
  isTutorialComplete: boolean;
  isFirstTimeUser: boolean;
  walkthroughActive: boolean;
  guidance: TutorialGuidance | null;
  skip: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(
  undefined,
);

export const TutorialProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isTutorialComplete, setIsTutorialComplete] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [walkthroughActive, setWalkthroughActive] = useState(false);

  const currentWord = useGameStore((s) => s.currentWord);
  // The Local Optimal route from the current word (best route to the target).
  const suggestedPathFromCurrent = useGameStore(
    (s) => s.suggestedPathFromCurrent,
  );
  const targetWord = useGameStore((s) => s.targetWord);
  const startWord = useGameStore((s) => s.startWord);
  const startGame = useGameStore((s) => s.startGame);
  const gameStatus = useGameStore((s) => s.gameStatus);
  const optimalChoices = useGameplayStore((s) => s.optimalChoices);

  // First-run detection: activate the guided-walkthrough banner for never-played
  // users. The tutorial GAME itself is started by the app flow
  // (GameFlowManager's "tutorial" case) — starting it here too would race it.
  useEffect(() => {
    (async () => {
      try {
        const complete = await unifiedDataStore.isTutorialComplete();
        const data = await unifiedDataStore.getData();
        const hasPlayedBefore = data.user.hasPlayedBefore;
        setIsTutorialComplete(complete);
        setIsFirstTimeUser(!hasPlayedBefore);
        if (!hasPlayedBefore && !complete) {
          setWalkthroughActive(true);
        }
      } catch (error) {
        console.error("Error checking tutorial status:", error);
      }
    })();
  }, []);

  // Mark the tutorial finished and hide the banner. The tutorial game is left on
  // its normal end-state screen (won or given-up) with its game report — no
  // re-init, so it behaves like any other game. (Only a Skip before the first move
  // discards the game and starts the next one; see skip().)
  const complete = async () => {
    setWalkthroughActive(false);
    setIsTutorialComplete(true);
    try {
      await unifiedDataStore.setTutorialComplete(true);
    } catch (error) {
      console.error("Error saving tutorial status:", error);
    }
  };

  // Skip: after a move, the tutorial game ends like a give-up (normal report
  // overlay, recorded like any game). Before the first move nothing was played,
  // so it is abandoned (nothing recorded) and the next game starts — today's
  // daily when available. If it already ended (won) or is loading, just finish.
  const skip = async () => {
    const outcome = await skipTutorialGame();
    await complete();
    if (outcome === "discarded") {
      await startGame();
    }
  };

  // Reaching the target shows her Done card on the won board; it stays until
  // Done is tapped (owner, 2026-10-21: the card is read, not timed). If the
  // player starts the next game instead, the walkthrough completes then.
  const arrivedRef = useRef(false);
  useEffect(() => {
    if (!walkthroughActive) return;
    if (targetWord && currentWord === targetWord) {
      arrivedRef.current = true;
    } else if (arrivedRef.current && gameStatus === "playing") {
      arrivedRef.current = false;
      complete().catch(() => undefined);
    }
  }, [walkthroughActive, currentWord, targetWord, gameStatus]);

  // A give-up during the walkthrough ends the tutorial too; otherwise the banner
  // would keep pointing at word options that are no longer on screen.
  useEffect(() => {
    if (walkthroughActive && gameStatus === "given_up") {
      complete();
    }
  }, [walkthroughActive, gameStatus]);

  // Tip cursor for the current tutorial game. Advanced during render (it is
  // idempotent for an unchanged move count) so the banner never shows a stale
  // frame; reset when a different game (start/target pair) is on the board.
  const gameKey = `${startWord ?? ""}|${targetWord ?? ""}`;
  const tipRef = useRef<{ key: string; progress: TipProgress }>({
    key: gameKey,
    progress: INITIAL_TIP_PROGRESS,
  });
  if (tipRef.current.key !== gameKey) {
    tipRef.current = { key: gameKey, progress: INITIAL_TIP_PROGRESS };
  }
  tipRef.current.progress = advanceTipProgress(
    tipRef.current.progress,
    optimalChoices.length,
  );

  const guidance = walkthroughActive
    ? tutorialGuidance(
        currentWord,
        suggestedPathFromCurrent,
        targetWord,
        optimalChoices,
        tipRef.current.progress,
      )
    : null;

  return (
    <TutorialContext.Provider
      value={{
        isTutorialComplete,
        isFirstTimeUser,
        walkthroughActive,
        guidance,
        skip,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
};
